import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost } from '../lib/api'
import {
  dmThreadsKey,
  dmThreadKey,
  appendDirectMessage,
  updateDirectMessageReactions,
} from '../lib/directMessages'
import type {
  ChatReactionEmoji,
  DirectMessageReactionUpdateResponse,
  DirectMessageResponse,
  DirectMessageThreadResponse,
  DirectMessageThreadsResponse,
} from '@warcarp/api-types'

/**
 * Thread summaries for the game (per-partner last message + unread count).
 * Drives the roster unread badges + the aggregate dot. The websocket keeps it
 * fresh by invalidating on DIRECT_MESSAGE; a light poll covers a dropped socket.
 */
export function useDmThreads(gameId: string, enabled = true) {
  return useQuery({
    queryKey: dmThreadsKey(gameId),
    queryFn: () =>
      apiGet<DirectMessageThreadsResponse>(`/api/v1/games/${gameId}/dms`).then((r) => r.threads),
    enabled: !!gameId && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}

/** The full conversation with one other player. */
export function useDmThread(gameId: string, otherUserId: string, enabled = true) {
  return useQuery({
    queryKey: dmThreadKey(gameId, otherUserId),
    queryFn: () =>
      apiGet<DirectMessageThreadResponse>(`/api/v1/games/${gameId}/dms/${otherUserId}`),
    enabled: !!gameId && !!otherUserId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
}

export function usePostDm(gameId: string, otherUserId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ content }: { content: string }) =>
      apiPost<DirectMessageResponse>(`/api/v1/games/${gameId}/dms/${otherUserId}`, { content }),
    onError: () => { void qc.invalidateQueries({ queryKey: dmThreadKey(gameId, otherUserId) }) },
    onSuccess: (msg) => {
      // Dedupe with the WS echo (server publishes before the HTTP reply flushes).
      qc.setQueryData<DirectMessageThreadResponse>(dmThreadKey(gameId, otherUserId), (old) =>
        appendDirectMessage(old, msg),
      )
      // Refresh the summary list so the thread reorders / last-message updates.
      void qc.invalidateQueries({ queryKey: dmThreadsKey(gameId) })
    },
  })
}

export function useToggleDmReaction(gameId: string, otherUserId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: ChatReactionEmoji }) =>
      apiPost<DirectMessageReactionUpdateResponse>(
        `/api/v1/games/${gameId}/dms/${otherUserId}/${messageId}/reactions`,
        { emoji },
      ),
    onSuccess: ({ messageId, reactions }) => {
      qc.setQueryData<DirectMessageThreadResponse>(dmThreadKey(gameId, otherUserId), (old) =>
        updateDirectMessageReactions(old, messageId, reactions),
      )
    },
  })
}

/** Mark a partner's messages read (clears their unread badge). */
export function useMarkDmRead(gameId: string, otherUserId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<{ marked: number }>(`/api/v1/games/${gameId}/dms/${otherUserId}/read`),
    // Optimistically stamp readAt on the partner's messages in the thread cache
    // immediately. Otherwise a close→reopen (which can serve the cached thread
    // before the refetch lands) renders the old readAt=null and resurrects the
    // "NEW" divider for messages already seen.
    onMutate: () => {
      const now = new Date().toISOString()
      qc.setQueryData<DirectMessageThreadResponse>(dmThreadKey(gameId, otherUserId), (old) => {
        if (!old) return old
        return {
          ...old,
          messages: old.messages.map((m) =>
            m.fromUserId === otherUserId && m.readAt === null ? { ...m, readAt: now } : m,
          ),
        }
      })
    },
    onSuccess: () => {
      // Refresh the badge summaries (server truth for the roster unread count).
      void qc.invalidateQueries({ queryKey: dmThreadsKey(gameId) })
    },
  })
}
